import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { get_api as getApi } from '../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../utils/urls';
import CustomModal from '../../util_components/custom_modal';
import {
  history_event_label as eventLabel,
  history_points_text as pointsText,
  history_actor_text as actorText,
  history_pricing_text as pricingText,
  history_exception_text as exceptionText
} from './history_utils';

// PROD-9786: one teacher's points, ban and exception history on one session.
function SessionHistoryModal({ teacherUuid, teacherName, session, ianaTimezone, onClose }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const sessionUuid = session ? session.session_uuid : '';

  useEffect(() => {
    if (!sessionUuid) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setHistory(null);
    const url = getApiUrl(
      `/v2/cms_user/trial_points/${teacherUuid}/sessions/${sessionUuid}/history`,
      '',
      true
    );
    getApi(url, true)
      .then((res) => {
        if (!cancelled) setHistory(res?.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load the history. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [teacherUuid, sessionUuid]);

  const formatTime = (at) =>
    at ? moment(at).tz(ianaTimezone).format('MMM DD, YYYY · hh:mm A z') : '';
  const events = history?.events || [];

  return (
    <CustomModal show={!!session} close={onClose}>
      <div className="p-4" style={{ color: 'var(--text-primary)', minWidth: '640px' }}>
        <h3 style={{ marginBottom: '4px' }}>Points history</h3>
        <div style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '12px' }}>
          {teacherName}
          {session?.student_name ? ` · ${session.student_name}` : ''}
          {session?.session_start ? ` · ${formatTime(session.session_start)}` : ''}
        </div>
        {loading ? <div>Loading…</div> : null}
        {error ? <div style={{ color: 'var(--state-error)' }}>{error}</div> : null}
        {!loading && !error && history ? (
          <>
            {events.length === 0 ? (
              <div>No points changes on this session.</div>
            ) : (
              <div className="tp-table">
                <div className="tp-row tp-head">
                  <div className="tp-col tp-left tp-wide">When</div>
                  <div className="tp-col tp-left">Event</div>
                  <div className="tp-col">Points</div>
                  <div className="tp-col tp-left tp-wide">Details</div>
                  <div className="tp-col">Net charge</div>
                </div>
                {events.map((ev, i) => (
                  // Events have no id of their own; the list is fixed once loaded.
                  // eslint-disable-next-line react/no-array-index-key
                  <div className="tp-row" key={`${ev.at}-${ev.kind}-${i}`}>
                    <div className="tp-col tp-left tp-wide">{formatTime(ev.at)}</div>
                    <div className="tp-col tp-left" style={{ fontWeight: 600 }}>
                      {eventLabel(ev)}
                    </div>
                    <div
                      className="tp-col"
                      style={{
                        fontWeight: 600,
                        color: ev.points > 0 ? 'var(--state-success)' : 'var(--state-error)'
                      }}
                    >
                      {pointsText(ev) || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </div>
                    <div className="tp-col tp-left tp-wide" style={{ fontSize: '13px' }}>
                      {[pricingText(ev), actorText(ev), ev.reason ? `"${ev.reason}"` : '']
                        .filter(Boolean)
                        .join(' · ') || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </div>
                    <div className="tp-col">
                      {ev.net_after ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginTop: '12px', fontSize: '14px' }}>
              <b>Current exception:</b>{' '}
              {history.exception ? (
                <>
                  {exceptionText(history.exception)} · &quot;{history.exception.reason}&quot;
                </>
              ) : (
                'None'
              )}
            </div>
          </>
        ) : null}
      </div>
    </CustomModal>
  );
}

SessionHistoryModal.propTypes = {
  teacherUuid: PropTypes.string.isRequired,
  teacherName: PropTypes.string,
  session: PropTypes.shape({
    session_uuid: PropTypes.string,
    student_name: PropTypes.string,
    session_start: PropTypes.string
  }),
  ianaTimezone: PropTypes.string,
  onClose: PropTypes.func.isRequired
};

SessionHistoryModal.defaultProps = {
  teacherName: '',
  session: null,
  ianaTimezone: undefined
};

export default SessionHistoryModal;
